<template>
  <div class="index-page">
    <header
      v-if="showPageHeader"
      class="page-header"
      :class="{ 'page-header--light': isLightHeader, 'page-header--auth': isAuthPage }"
    >
      <button
        v-if="showBackButton"
        type="button"
        class="page-back"
        aria-label="返回上一页"
        @click="goBack"
      >
        ‹
      </button>
      <h1 class="page-title">{{ pageTitle }}</h1>
    </header>

    <div ref="indexBody" class="index-body" :class="{ 'index-body--immersive': isImmersivePage }">
      <keep-alive include="ManualPlan">
        <router-view
          :key="routeViewKey"
          :categoryId="routeCategoryId"
          @homepage-version-change="handleHomepageVersionChange"
        />
      </keep-alive>
    </div>

    <nav v-if="showTabBar" class="tab-bar" aria-label="底部导航">
      <button
        type="button"
        class="tab-item"
        :class="{ active: activeTab === 'home' }"
        @click="goTab('home')"
      >
        <svg class="tab-icon" viewBox="0 0 24 24" aria-hidden="true">
          <path
            fill="none"
            stroke="currentColor"
            stroke-width="1.75"
            stroke-linecap="round"
            stroke-linejoin="round"
            d="M3 10.5L12 4l9 6.5V20a1 1 0 0 1-1 1h-5v-6H9v6H4a1 1 0 0 1-1-1v-9.5z"
          />
        </svg>
        <span class="tab-label">首页</span>
      </button>
      <button
        type="button"
        class="tab-item"
        :class="{ active: activeTab === 'live' }"
        @click="goTab('live')"
      >
        <svg class="tab-icon" viewBox="0 0 24 24" aria-hidden="true">
          <rect
            x="3"
            y="6"
            width="11"
            height="12"
            rx="2"
            fill="none"
            stroke="currentColor"
            stroke-width="1.75"
          />
          <path
            fill="none"
            stroke="currentColor"
            stroke-width="1.75"
            stroke-linecap="round"
            stroke-linejoin="round"
            d="M16 10l5-3v10l-5-3v-4z"
          />
        </svg>
        <span class="tab-label">直播</span>
      </button>
      <button
        type="button"
        class="tab-item"
        :class="{ active: activeTab === 'tool' }"
        @click="goTab('tool')"
      >
        <svg class="tab-icon" viewBox="0 0 24 24" aria-hidden="true">
          <rect
            x="4"
            y="4"
            width="6"
            height="6"
            rx="1.4"
            fill="none"
            stroke="currentColor"
            stroke-width="1.75"
          />
          <rect
            x="14"
            y="4"
            width="6"
            height="6"
            rx="1.4"
            fill="none"
            stroke="currentColor"
            stroke-width="1.75"
          />
          <rect
            x="4"
            y="14"
            width="6"
            height="6"
            rx="1.4"
            fill="none"
            stroke="currentColor"
            stroke-width="1.75"
          />
          <rect
            x="14"
            y="14"
            width="6"
            height="6"
            rx="1.4"
            fill="none"
            stroke="currentColor"
            stroke-width="1.75"
          />
        </svg>
        <span class="tab-label">工具</span>
      </button>
      <button
        type="button"
        class="tab-item"
        :class="{ active: activeTab === 'predict' }"
        @click="goTab('predict')"
      >
        <svg class="tab-icon" viewBox="0 0 24 24" aria-hidden="true">
          <path
            fill="none"
            stroke="currentColor"
            stroke-width="1.75"
            stroke-linecap="round"
            stroke-linejoin="round"
            d="M5 6h14a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2h-4l-4 3v-3H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2z"
          />
          <circle cx="9" cy="11" r="0.9" fill="currentColor" stroke="none" />
          <circle cx="12" cy="11" r="0.9" fill="currentColor" stroke="none" />
          <circle cx="15" cy="11" r="0.9" fill="currentColor" stroke="none" />
        </svg>
        <span class="tab-label">预测</span>
      </button>
      <button
        type="button"
        class="tab-item"
        :class="{ active: activeTab === 'mine' }"
        @click="goTab('mine')"
      >
        <svg class="tab-icon" viewBox="0 0 24 24" aria-hidden="true">
          <circle cx="12" cy="8.5" r="3.25" fill="none" stroke="currentColor" stroke-width="1.75" />
          <path
            fill="none"
            stroke="currentColor"
            stroke-width="1.75"
            stroke-linecap="round"
            d="M6 19.5v-.5a6 6 0 0 1 12 0v.5"
          />
        </svg>
        <span class="tab-label">我的</span>
      </button>
    </nav>
  </div>
</template>

<script>
import { isHomeRoute, prepareHomeTabLeave } from '@/utils/homeNav'
import { isPredictListRoute, preparePredictListTabLeave } from '@/utils/predictListNav'

export default {
  name: 'AppIndex',
  data() {
    return {
      homepageVersion: ''
    }
  },
  watch: {
    /**
     * 功能：在路由切换到全屏沉浸页时重置外层滚动容器位置，避免沿用上一页滚动把子页头部顶出可视区域。
     * 参数：无。
     */
    '$route.fullPath'() {
      this.$nextTick(() => {
        if (this.isImmersivePage) this.resetBodyScroll()
      })
    }
  },
  computed: {
    routeViewKey() {
      return ['/predict', '/predict/selected', '/predict/ranking'].includes(this.$route.path)
        ? this.$route.path
        : this.$route.fullPath
    },
    activeTab() {
      const p = this.$route.path || ''
      if (p.startsWith('/live')) return 'live'
      if (p.startsWith('/tool') || p.startsWith('/shrink-tool') || p.startsWith('/trend')) return 'tool'
      if (p.startsWith('/predict')) return 'predict'
      if (p.startsWith('/mine')) return 'mine'
      return 'home'
    },
    routeCategoryId() {
      const id = Number(this.$route.query.categoryId)
      return Number.isNaN(id) ? 0 : id
    },
    showPageHeader() {
      const p = this.$route.path || '/'
      if (p === '/') return this.homepageVersion === 'new'
      if (p === '/install') return false
      if (p === '/manual-plan/detail') return false
      if (p === '/mine/join') return false
      if (p === '/predict/scheme') return false
      if (p === '/predict/rule-publish') return false
      if (p === '/tool/bet-multiplier' || p === '/tool/wave-calculator' || p === '/tool/probability-calculator' || p === '/tool/golden-split' || p === '/tool/base-validation') return false
      if (p === '/shrink-tool/filter-king' || p === '/shrink-tool/filter-king-five' || p === '/shrink-tool/three' || p === '/shrink-tool/three/result' || p === '/shrink-tool/help' || p === '/trend/fc3d') return false
      return p !== '/predict' && p !== '/mine'
    },
    showTabBar() {
      const p = this.$route.path || ''
      if (p.startsWith('/tool/')) return false
      return !['/manual-plan', '/manual-plan/detail', '/predict/publish', '/predict/rule-publish', '/predict/scheme', '/predict/search', '/predict/author', '/predict/experts', '/predict/selected', '/predict/ranking', '/predict/my-articles', '/predict/pay-protocol', '/login', '/register', '/forgot-password', '/agreement/user', '/agreement/privacy', '/install', '/draw-rule', '/shrink-tool', '/shrink-tool/filter-king', '/shrink-tool/filter-king-five', '/shrink-tool/three', '/shrink-tool/three/result', '/shrink-tool/help', '/trend/fc3d', '/mine/join'].includes(p)
    },
    showBackButton() {
      const p = this.$route.path || ''
      return ['/manual-plan', '/community', '/detail/LotteryHistory', '/detail/Oneiromancy', '/detail/OneiromancyResult', '/predict/publish', '/predict/search', '/predict/detail', '/predict/author', '/predict/experts', '/predict/selected', '/predict/ranking', '/predict/my-articles', '/predict/pay-protocol', '/login', '/register', '/forgot-password', '/agreement/user', '/agreement/privacy', '/draw-rule', '/shrink-tool', '/trend/fc3d'].includes(p) || p.startsWith('/mine/') || p.startsWith('/tool/')
    },
    isLightHeader() {
      const p = this.$route.path || ''
      return ['/predict/publish', '/predict/search', '/predict/detail', '/predict/author', '/predict/experts', '/predict/selected', '/predict/ranking', '/predict/my-articles', '/predict/pay-protocol', '/agreement/user', '/agreement/privacy', '/draw-rule', '/shrink-tool', '/trend/fc3d'].includes(p) || p.startsWith('/mine/') || p.startsWith('/tool/')
    },
    isAuthPage() {
      const p = this.$route.path || ''
      return ['/login', '/register', '/forgot-password'].includes(p)
    },
    /** 画规/缩水/长条等全屏内容页：由子页面内部滚动，避免与外层双滚动 */
    isImmersivePage() {
      const p = this.$route.path || ''
      return ['/manual-plan', '/manual-plan/detail', '/draw-rule', '/shrink-tool', '/shrink-tool/filter-king', '/shrink-tool/filter-king-five', '/shrink-tool/three', '/shrink-tool/three/result', '/shrink-tool/help', '/trend/fc3d', '/tool/bet-multiplier', '/tool/wave-calculator', '/tool/probability-calculator', '/tool/golden-split', '/tool/base-validation', '/detail/LotteryHistory', '/predict/scheme', '/predict/rule-publish', '/mine/ledger'].includes(p)
    },
    pageTitle() {
      if (this.$route.query && this.$route.query.title) {
        return this.$route.query.title
      }
      const meta = this.$route.meta
      if (typeof meta === 'string') return meta
      if (meta && meta.title) return meta.title
      return '页面'
    }
  },
  methods: {
    /** 根据主页入口读取到的后台配置控制新版首页标题栏。 */
    handleHomepageVersionChange(version) {
      this.homepageVersion = version === 'new' ? 'new' : 'legacy'
    },
    /**
     * 功能：重置外层页面滚动容器的滚动位置。
     * 参数：无。
     */
    resetBodyScroll() {
      const body = this.$refs.indexBody
      if (body && typeof body.scrollTop === 'number') {
        body.scrollTop = 0
      }
    },
    /** 底部 Tab 切换；离开预测列表时先快照状态再跳转 */
    goTab(tab) {
      const fromPath = this.$route.path
      const leavingHome = isHomeRoute(fromPath)
      const leavingPredictList = isPredictListRoute(fromPath)

      if (tab === 'home') {
        if (leavingPredictList) {
          preparePredictListTabLeave()
        }
        if (fromPath !== '/') {
          this.$router.push('/').catch(() => {})
        }
        return
      }
      const paths = {
        live: '/live',
        tool: '/tool',
        predict: '/predict',
        mine: '/mine'
      }
      const target = paths[tab]
      if (!target || fromPath === target) {
        return
      }
      if (leavingHome) {
        prepareHomeTabLeave()
      }
      if (leavingPredictList) {
        preparePredictListTabLeave()
      }
      this.$router.push(target).catch(() => {})
    },
    /** 顶栏返回：有历史则后退，否则回首页 */
    goBack() {
      if (window.history.length > 1) {
        this.$router.back()
        return
      }
      this.$router.push('/').catch(() => {})
    }
  }
};
</script>

<style scoped>
.index-page {
  display: flex;
  flex-direction: column;
  height: 100%;
  height: calc(var(--app-vh, 1vh) * 100);
  background-color: #ffffff;
  box-sizing: border-box;
}
.page-header {
  position: relative;
  flex-shrink: 0;
  padding: calc(14px + env(safe-area-inset-top, 0px)) 16px 14px;
  background: #ffffff;
  color: #111;
  border-bottom: 1px solid #f0f0f0;
  text-align: center;
  box-sizing: border-box;
}
.page-header--light {
  background: #ffffff;
  color: #111;
  border-bottom: 1px solid #f0f0f0;
}
.page-header--auth {
  background: #ffffff;
  color: #111;
  border-bottom: 1px solid #f0f0f0;
}
.page-back {
  position: absolute;
  left: 1px;
  bottom: 7px;
  width: 36px;
  height: 36px;
  padding: 0;
  border: none;
  background: transparent;
  color: #111;
  font-size: 0;
  line-height: 32px;
  font-weight: 300;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
.page-back::before {
  content: '';
  position: absolute;
  left: 14px;
  top: 10px;
  width: 11px;
  height: 11px;
  border-left: 2px solid currentColor;
  border-bottom: 2px solid currentColor;
  transform: rotate(45deg);
}
.page-header--light .page-back {
  color: #111;
}
.page-header--auth .page-back {
  color: #111;
}
.page-title {
  margin: 0;
  font-size: 17px;
  font-weight: 700;
  line-height: 1.3;
}
.index-body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  -ms-overflow-style: none;
}
.index-body::-webkit-scrollbar {
  display: none;
}
.index-body--immersive {
  overflow: hidden;
  display: flex;
  flex-direction: column;
  min-height: 0;
}
.index-body--immersive > * {
  flex: 1;
  min-height: 0;
}
.tab-bar {
  flex-shrink: 0;
  display: flex;
  justify-content: space-around;
  align-items: stretch;
  padding-bottom: 10px;
  background: #fff;
  border-top: 1px solid #e8e8e8;
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.04);
}
.tab-item {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 4px 4px 2px;
  border: none;
  background: transparent;
  color: #8a8a8a;
  font-size: 11px;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
.tab-item.active {
  color: #932727;
}
.tab-icon {
  width: 24px;
  height: 24px;
}
.tab-label {
  line-height: 1.2;
}
</style>
